export enum POIsClassification {
  'Category' = 'categories',
  'Brand' = 'brands',
  'Locations' = 'locations',
  'CustomPOIs' = 'custom',
  'Tags' = 'tags',
}

export enum POIsFilterMethod {
  'Radius' = 'RADIUS',
  'Walking' = 'WALKING',
  'Driving' = 'DRIVING',
}

export enum POIsFilterOperation {
  Union = 'union',
  Intersection = 'intersection',
}

export interface Category {
  top_category: string
  sub_category: string
  occurrences: number
}

export interface CustomPOIsGroupId {
  id: string
}

export interface CustomPOIsResponse {
  rows_inserted?: number
  rows_deleted?: number
  rows_reactivated?: number
  rows_updated?: number
  errors?: Error[]
  message?: string
}

interface CustomPOIsGroupExpiration {
  expiration?: Date
  expired?: boolean
}

interface CustomPOIsGroupBasic {
  id: string
  name: string
  shared: boolean
  user_email: string
  modified?: string | Date
  description?: string
  countries?: number[]
}

export interface CustomPOIsGroup
  extends CustomPOIsGroupBasic,
    Category,
    CustomPOIsGroupExpiration {}

export interface Location {
  name: string
  normalized_name: string
  occurrences: number
  country_id: number
}

export interface Brand {
  brands: string
  occurrences: number
}

export interface Tag {
  tags: string
  occurrences: number
}

export interface PoiTag {
  id: string
  name: string
  type: string
}

export interface Geoid {
  geoid: string
}

export interface TypesOfPOIs {
  categories: string[]
  brands: string[]
  tags: string[]
  locations: string[]
  custom_POIs: string[]
}

export interface FilterByPoisParamsRequest extends TypesOfPOIs {
  operation: POIsFilterOperation
  max_distance: number
  min_distance: number
  distance: number
  method: POIsFilterMethod
  max_assets_per_poi: number
}

export interface CustomPOI {
  name: string
  geom: string
  address?: string
}

export interface CustomPOIConvertedByCarto {
  name: string
  geom: {
    coordinates: number[]
  }
  address?: string
}

export interface CustomPOIsDataset {
  pois: CustomPOI[]
}

export interface CustomPOIsGroupRequest extends CustomPOIsGroupBasic {
  customPOIs: CustomPOI[]
  description: string
  sub_categories: string[]
  action?: 'create' | 'update' | 'exist'
}

export interface CustomPOIsDatasetParamsRequest {
  id: string
  user_email: string
}

export interface CustomPOIAddress {
  name: string
  country: string
  address: string
}
export interface CustomPOIsAddressGroupRequest extends CustomPOIsGroupBasic {
  description: string
  sub_categories: string[]
  action: string
  customPOIsAddress: CustomPOIAddress[]
}

export enum CustomPOIsImportMode {
  'Coordinates' = 'COORDINATES',
  'Geocode' = 'GEOCODE',
}

export interface CustomPOIsUnableToGeocode {
  name: string
  address: string
  carto_geocode_metadata: string
}
export interface CustomPOIsGeocoded {
  total: number
  geocoded: number
  unableToGeocode?: CustomPOIsUnableToGeocode[]
}

/**
 * How an uploaded logo is fitted inside the map marker pin: `whole` shows the
 * complete logo (contain), `crop` fills the pin head and crops the overflow
 * (cover).
 */
export type PoiIconFit = 'whole' | 'crop'

export const POI_ICON_FITS: readonly PoiIconFit[] = ['whole', 'crop']

/** Fit applied when a logo is first uploaded or a stored value carries none. */
export const DEFAULT_POI_ICON_FIT: PoiIconFit = 'whole'

export function isPoiIconFit(value: unknown): value is PoiIconFit {
  return POI_ICON_FITS.includes(value as PoiIconFit)
}

/**
 * A logo resolved to its public URL plus the marker fit to compose it with —
 * both the shared-plan cloud function's response entries and the frontend's
 * per-selection logo sources.
 */
export interface PoiIconUrlEntry {
  url: string
  fit: PoiIconFit
}

export interface PoiIconAssignment {
  /** Uploaded logo file basename (`<uuid>.<ext>`). */
  fileName: string
  fit: PoiIconFit
}

/**
 * POI selection key (same keys as `poisColors`: brand, category tag, location
 * name, category or custom-group id) -> logo assignment. The full image URL is
 * derived at runtime from the environment bucket and the plan id, so only the
 * basename is persisted. Plans saved before the fit option stored a bare
 * basename string per key; readers normalize those to `fit: 'whole'`.
 */
export type PoisIcons = Record<string, PoiIconAssignment>
